// Lightweight topic extractor without LLMs
// - Extract hashtags as explicit topics (source: 'hashtag')
// - Extract simple semantic topics by normalizing words and filtering stopwords (source: 'semantic')

const STOPWORDS = new Set([
  'the','a','an','and','or','but','if','then','else','of','in','on','for','to','with','at','by','from','up','down','over','under','is','are','was','were','be','been','being','it','its','as','that','this','these','those','you','your','we','our','they','their','i','me','my','mine','he','she','him','her','his','hers','them','theirs','not','no','yes','do','did','does','doing','have','has','had'
]);

function normalizeToken(tok: string): string | null {
  const t = tok.toLowerCase().replace(/[^a-z0-9+]+/g, '');
  if (!t || t.length < 2) return null;
  if (STOPWORDS.has(t)) return null;
  // Avoid pure numbers
  if (/^\d+$/.test(t)) return null;
  return t;
}

export interface ExtractedTopic {
  topic: string;
  confidence: number; // 0..1
  source: 'semantic' | 'hashtag';
}

export function extractHashtags(text?: string): string[] {
  if (!text) return [];
  const tags = text.match(/#\w+/g);
  return tags ? Array.from(new Set(tags.map(t => t.slice(1).toLowerCase()))) : [];
}

export function inferSemanticTopics(text?: string, limit = 5): ExtractedTopic[] {
  if (!text) return [];
  // Tokenize by non-word boundaries
  const tokens = text.split(/\s+/g)
    .map(normalizeToken)
    .filter((t): t is string => !!t);
  if (!tokens.length) return [];

  // Count frequencies
  const freq = new Map<string, number>();
  for (const t of tokens) freq.set(t, (freq.get(t) || 0) + 1);

  // Score by frequency and token length (favor more descriptive tokens)
  const scored = Array.from(freq.entries())
    .map(([t, c]) => ({ t, score: c * Math.min(1, t.length / 8) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  // Normalize to confidence 0.4..0.9 range
  const max = scored[0]?.score || 1;
  return scored.map(({ t, score }) => ({
    topic: t,
    confidence: 0.4 + 0.5 * (score / max),
    source: 'semantic' as const,
  }));
}

export function inferTopicsFromText(text?: string): ExtractedTopic[] {
  const sem = inferSemanticTopics(text);
  const tags = extractHashtags(text).map((h) => ({ topic: h, confidence: 0.8, source: 'hashtag' as const }));
  return [...sem, ...tags];
}
